/**
 * AllysListingGrid - Allys-themed Listing Grid Component
 * 
 * Uses AllysListingCard for consistent Allys styling with:
 * - Navy (#243462) and Gold (#BBA339) color scheme
 * - Agent avatars with name popovers
 * - Modern card design with hover effects
 */

'use client';

import type { ListingCardData } from '@/components/listings/types';
import { AllysListingCard } from './AllysListingCard';

export interface AllysListingGridProps {
  listings: ListingCardData[];
  basePath: string;
  variant?: 'grid' | 'list';
  columns?: 1 | 2 | 3 | 4;
  showAgent?: boolean;
  showAgentAvatar?: boolean;
  showStatus?: boolean;
  showWishlistButton?: boolean;
  wishlistEntityType?: 'PROPERTY' | 'LISTING';
  locale?: string;
  messages?: Record<string, unknown>;
  emptyMessage?: string;
  emptyDescription?: string;
  className?: string;
}

export function AllysListingGrid({
  listings,
  basePath,
  variant = 'grid',
  columns = 3,
  showAgent = false,
  showAgentAvatar = true,
  showStatus = true,
  showWishlistButton = true,
  wishlistEntityType = 'PROPERTY',
  locale = 'en',
  messages,
  emptyMessage = 'No listings found',
  emptyDescription = 'Check back later for new listings.',
  className = '',
}: AllysListingGridProps) {
  // Empty state - Allys styled
  if (listings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center font-inter">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#243462]/10 to-[#BBA339]/10 flex items-center justify-center mb-6">
          <svg className="w-10 h-10 text-[#243462]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-[#243462] mb-2">{emptyMessage}</h3>
        <p className="text-gray-500 max-w-md">{emptyDescription}</p>
      </div>
    );
  }

  // Grid layout
  const gridClasses = variant === 'list' 
    ? 'flex flex-col gap-4'
    : `grid gap-6 ${
        columns === 1 ? 'grid-cols-1' :
        columns === 2 ? 'grid-cols-1 md:grid-cols-2' :
        columns === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' :
        'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      }`;

  return (
    <div className={`${gridClasses} ${className}`}>
      {listings.map((listing) => (
        <AllysListingCard
          key={listing.id}
          listing={listing}
          href={`${basePath}/properties/${listing.property.id}`}
          variant={variant === 'list' ? 'compact' : 'default'}
          showAgent={showAgent}
          showAgentAvatar={showAgentAvatar}
          showStatus={showStatus}
          showWishlistButton={showWishlistButton}
          wishlistEntityType={wishlistEntityType}
          locale={locale}
          {...(messages !== undefined && { messages })}
        />
      ))}
    </div>
  );
}

/**
 * Loading skeleton for AllysListingGrid
 */
export function AllysListingGridSkeleton({
  count = 6,
  columns = 3,
  className = '',
}: {
  count?: number;
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}) {
  const gridClasses = `grid gap-6 ${
    columns === 1 ? 'grid-cols-1' :
    columns === 2 ? 'grid-cols-1 md:grid-cols-2' :
    columns === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' :
    'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
  }`;

  return (
    <div className={`${gridClasses} ${className}`}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 animate-pulse">
          {/* Image skeleton */}
          <div className="aspect-[4/3] bg-gradient-to-br from-gray-200 to-gray-100" />
          
          {/* Content skeleton */}
          <div className="p-5 space-y-3">
            {/* Location skeleton */}
            <div className="h-3 bg-gray-200 rounded-full w-1/2" />
            
            {/* Title skeleton */}
            <div className="h-5 bg-gray-200 rounded-lg w-3/4" />
            
            {/* Price skeleton */}
            <div className="h-7 bg-[#243462]/10 rounded-lg w-1/3" />
            
            {/* Features skeleton */}
            <div className="flex gap-4 py-2 px-3 bg-gray-50 rounded-xl">
              <div className="h-4 bg-gray-200 rounded w-14" />
              <div className="h-4 bg-gray-200 rounded w-14" />
              <div className="h-4 bg-gray-200 rounded w-20" />
            </div>
            
            {/* CTA skeleton */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <div className="h-4 bg-gray-200 rounded w-20" />
              <div className="w-8 h-8 bg-[#243462]/10 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default AllysListingGrid;
